import { Box, CircularProgress, ImageListItem } from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntersectionObserverRef } from "rooks";
import { CachePolicies } from "use-http";

import { STATUS } from "components/detector/Activity";
import { PageContext } from "components/detector/ImageQueryPreview";
import { ImageQueryView } from "components/detector/ImageQueryView";
import { JANZU_URL } from "config/config";
import { QueryDetailsModal } from "features/queryDetailsModal/QueryDetailsModal";
import { useFetchCustom } from "hooks/useFetchCustom";
import { Typography } from "new-components/typography/Typography";
import { ListResponse } from "types/api";
import { Detector } from "types/detectors";
import { ImageQuery } from "types/imageQueries";
import { removeEmptyValuesFromObject } from "utils/redirectDataPage";
import { extractPaginateFrom } from "utils/utilityFunctions";

const mapSizes = {
  1: 5,
  2: 4,
  3: 3,
  4: 2,
};

const mapClassNamesHeight = {
  1: "h-[100px] md:h-[165px]",
  2: "h-[160px] md:h-[240px]",
  3: "h-[200px] md:h-[320px]",
  4: "h-[250px] md:h-[500px]",
};

export interface GridViewProps {
  gridSize: number;
  filtersObject: Record<string, string>;
  detectorData: Detector | undefined;
}

export function GridView(props: GridViewProps) {
  const { gridSize, filtersObject, detectorData } = props;

  const { enqueueSnackbar } = useSnackbar();

  const [dataList, setDataList] = useState<ImageQuery[]>([]);
  const [next, setNext] = useState("");

  const [showQueryDetailsModal, setShowQueryDetailsModal] = useState(false);
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(0);

  const rowMap: Map<string, ImageQuery> = useMemo(() => new Map<string, ImageQuery>([]), []);

  const imageQueryUrl = useMemo(() => {
    const imageQueriesUrl = `${JANZU_URL}reef-api/image-queries?`;
    const object: Record<string, string> = {
      ...filtersObject,
      paginate_from: next ? extractPaginateFrom(next) : "",
    };
    const queryString = new URLSearchParams(removeEmptyValuesFromObject(object)).toString();

    return `${imageQueriesUrl}${queryString}`;
  }, [filtersObject, next]);

  const {
    get: getIQList,
    data: IQData,
    loading: loadingIQList,
  } = useFetchCustom<ListResponse<ImageQuery>>(imageQueryUrl, {
    cachePolicy: CachePolicies.NO_CACHE,
  });

  // Intersection observer
  const fetchMore = useCallback(
    (entries) => {
      if (loadingIQList) return;

      if (entries && entries[0]) {
        if (entries[0].isIntersecting && IQData?.next) {
          setNext(IQData.next);
        }
      }
    },
    [IQData?.next, loadingIQList]
  );

  const [loadMoreRef] = useIntersectionObserverRef(fetchMore);

  const updateRows = useCallback(
    (data?: ImageQuery[]) => {
      const flattenedArray: ImageQuery[] = [];
      rowMap.clear();
      (data || [])?.forEach((value) => {
        const row = { ...value };
        rowMap.set(row.iq_id, row);
        flattenedArray.push(row);
      });
      setDataList(flattenedArray);
    },
    [rowMap]
  );

  useEffect(() => {
    const getData = () => {
      getIQList().then((response) => {
        if (response && response.results) {
          updateRows([...dataList, ...response.results]);
        }
      });
    };

    if ((next || dataList?.length === 0) && !loadingIQList) {
      if (!filtersObject.detector_id) {
        return;
      }

      getData();
    }
    // eslint-disable-next-line
  }, [next]);

  const updateSelectedRowIndex = (index: number) => {
    setSelectedRowIndex(index);
  };

  const updateShowQueryDetailsModal = (show: boolean) => {
    setShowQueryDetailsModal(show);
  };

  const getIndex = useCallback(
    (selectedData) => dataList.findIndex((row) => row?.iq_id === selectedData?.iq_id),
    [dataList]
  );

  useEffect(() => {
    if (selectedRowIndex && dataList.length > 0) {
      const row = rowMap.get(dataList[selectedRowIndex]?.iq_id);
      const rowData = row || dataList[dataList.length - 1] || dataList[0];

      const index = getIndex(rowData);
      updateSelectedRowIndex(index);
    }
  }, [dataList, selectedRowIndex, rowMap, getIndex]);

  const handleRowClick = (row: ImageQuery) => {
    const index = getIndex(row);
    updateShowQueryDetailsModal(true);
    updateSelectedRowIndex(index);
  };

  const fetchImageQueries = useCallback(async () => {
    setNext("");
    try {
      const response = await getIQList();
      if (response?.count !== undefined) {
        updateRows([...response.results]);
      }
      updateShowQueryDetailsModal(false);
    } catch (error) {
      enqueueSnackbar("Error fetching image queries", { variant: "error" });
    }
  }, [getIQList, updateRows, enqueueSnackbar]);

  const showLoadingIndicator = IQData?.next != null;

  return (
    <>
      {dataList && dataList?.length > 0 ? (
        <Box>
          <Box
            role="grid"
            display="grid"
            gridTemplateColumns={`repeat(${mapSizes[gridSize as keyof typeof mapSizes]}, minmax(0, 1fr))`}
            className="p-4 bg-white rounded-md"
            gap={4}
          >
            {dataList &&
              dataList.map((imageQuery, idx) => (
                <ImageListItem
                  key={imageQuery.iq_id}
                  ref={idx === dataList.length - 1 ? loadMoreRef : null}
                  onClick={() => handleRowClick(imageQuery)}
                  onKeyDown={() => handleRowClick(imageQuery)}
                  className="text-sm cursor-pointer"
                >
                  <ImageQueryView
                    classNames={mapClassNamesHeight[gridSize as keyof typeof mapClassNamesHeight]}
                    pageContext={PageContext.DetectorDetails}
                    imageQuery={imageQuery}
                  />
                </ImageListItem>
              ))}
          </Box>
          <Box className="mt-4 flex justify-center">{showLoadingIndicator && <CircularProgress />}</Box>
        </Box>
      ) : (
        <Box className="my-14 flex justify-center">
          {loadingIQList ? (
            <CircularProgress />
            ) : (
                <Typography variant="subtitle" align="center">
                  No Image Queries
                </Typography>
          )}
        </Box>
      )}

      {showQueryDetailsModal && rowMap.size > 0 && detectorData && (
        <QueryDetailsModal
          loading={loadingIQList}
          getData={fetchImageQueries}
          rows={dataList}
          rowCount={dataList?.length || 0}
          selectedRowIndex={selectedRowIndex}
          setSelectedRowIndex={setSelectedRowIndex}
          showDialog={showQueryDetailsModal && rowMap.size > 0}
          onClose={() => updateShowQueryDetailsModal(false)}
          page={1}
          pageSize={dataList?.length || 0}
          setPage={() => {}}
          setPageSize={() => {}}
          detector={detectorData}
          mode={STATUS.FLAGGED}
        />
      )}
    </>
  );
}
